/**
 * Discovery / Data Sources - supported-source directory + real connect flow.
 *
 * 2026-06-05 rework. Previously every "Add my X" card deep-linked to the
 * OUTBOUND marketplace (`/admin/integrations`), which has no data-source
 * connectors - so clicking "Add my PostgreSQL" dead-ended and nothing could
 * actually be connected. Now:
 *   • PostgreSQL / SQL Server / Oracle (the engines the controller can crawl
 *     directly, `POST /v1/discovery/catalog-crawl`) open an inline CONNECT
 *     modal that genuinely connects, enumerates the schema, and flags PII.
 *   • Every other source needs an Edge pod inside the customer network, so its
 *     card routes to Edge Fleet (the real path) instead of a dead link.
 *
 * Also fixes the registry field mismatch - the endpoint returns
 * `kind`/`discoveryCapability`/`configFields`, not `vendorKey`/`supportsScan`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  ArrowRight, Database, Cloud, Server, HardDrive, Loader2, AlertTriangle, Plug,
  Radio, Boxes, CheckCircle2, ShieldCheck, X, ScanLine,
} from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/primitives';
import {
  listDiscoveryConnectorRegistry, catalogCrawl,
  type DiscoveryConnector, type CatalogCrawlSummary,
} from '@/lib/api';

type CrawlVendor = 'postgres' | 'mssql' | 'oracle';

/**
 * Registry `kind` → catalog-crawl vendor, for the engines the controller can
 * connect to directly TODAY. These are the only sources with a working
 * self-serve connect flow; everything else needs an Edge pod.
 */
const CRAWL_VENDOR: Record<string, CrawlVendor> = {
  postgres: 'postgres',
  gcp_cloud_sql_postgres: 'postgres',
  mssql: 'mssql',
  azure_sql_database: 'mssql',
  azure_sql_managed_instance: 'mssql',
  gcp_cloud_sql_sqlserver: 'mssql',
  oracle: 'oracle',
};
const DEFAULT_PORT: Record<CrawlVendor, number> = { postgres: 5432, mssql: 1433, oracle: 1521 };

const ICON_FOR_CATEGORY: Record<DiscoveryConnector['category'], React.ComponentType<{ className?: string }>> = {
  relational: Database, warehouse: Server, nosql: Database, object_storage: HardDrive,
  m365: Cloud, workspace: Cloud, cache: Server, streaming: Radio, saas: Boxes,
};
const LABEL_FOR_CATEGORY: Record<DiscoveryConnector['category'], string> = {
  relational: 'Relational', warehouse: 'Warehouse', nosql: 'NoSQL', object_storage: 'Object storage',
  m365: 'Microsoft 365', workspace: 'Google Workspace', cache: 'Cache', streaming: 'Streaming', saas: 'SaaS',
};

export function DiscoveryConnectors() {
  const [connectors, setConnectors] = useState<DiscoveryConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<DiscoveryConnector['category'] | 'all'>('all');
  const [connectTarget, setConnectTarget] = useState<DiscoveryConnector | null>(null);

  const refresh = (signal?: AbortSignal) =>
    listDiscoveryConnectorRegistry()
      .then((res) => { if (!signal?.aborted) { setConnectors(res.connectors); setError(null); } })
      .catch((err) => { if (!signal?.aborted) setError(err instanceof Error ? err.message : 'Failed to load connector registry'); })
      .finally(() => { if (!signal?.aborted) setLoading(false); });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const filtered = useMemo(
    () => (categoryFilter === 'all' ? connectors : connectors.filter((c) => c.category === categoryFilter)),
    [connectors, categoryFilter],
  );
  const categories = useMemo(() => {
    const counts = new Map<DiscoveryConnector['category'], number>();
    for (const c of connectors) counts.set(c.category, (counts.get(c.category) ?? 0) + 1);
    return Array.from(counts.entries()).sort(([, a], [, b]) => b - a);
  }, [connectors]);
  const directCount = useMemo(() => connectors.filter((c) => CRAWL_VENDOR[c.kind]).length, [connectors]);

  return (
    <PageContainer>
      <PageHeader
        title="Data Sources"
        subtitle="Connect a source so Discovery can scan it for PII. Relational DBs connect directly here; everything else scans through an Edge pod in your network."
        badges={[
          { label: `${connectors.length} sources`, variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Degraded', variant: 'warning' as const }] : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" asChild>
            <Link to="/admin/edge-fleet"><Plug className="h-4 w-4" /> Edge Fleet</Link>
          </Button>
        }
      />

      {loading && <LoadingState caption="Loading data-source registry…" />}
      {error && !loading && (
        <ErrorState title="Could not load data-source registry" detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }} />
      )}

      {!loading && !error && (
        <>
          {/* Connect-path legend so the two paths are obvious up front. */}
          <div className="glass-lite rounded-md p-3 mb-4 text-xs flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><ScanLine className="h-3.5 w-3.5 text-success" /> <span className="text-foreground/80">{directCount} connect directly</span> <span className="text-muted-foreground">- PostgreSQL · SQL Server · Oracle (give read-only creds, scans now)</span></span>
            <span className="inline-flex items-center gap-1.5"><Plug className="h-3.5 w-3.5 text-primary" /> <span className="text-foreground/80">rest via Edge pod</span> <span className="text-muted-foreground">- runs inside your VPC, raw values never leave</span></span>
          </div>

          {/* Category filter chips */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setCategoryFilter('all')}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${categoryFilter === 'all' ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:text-foreground'}`}>
              All ({connectors.length})
            </button>
            {categories.map(([cat, count]) => (
              <button key={cat} onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${categoryFilter === cat ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:text-foreground'}`}>
                {LABEL_FOR_CATEGORY[cat]} ({count})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((c, i) => {
              const Icon = ICON_FOR_CATEGORY[c.category] ?? Database;
              const direct = !!CRAWL_VENDOR[c.kind];
              return (
                <motion.div key={c.kind} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.015, 0.4) }}>
                  <GlassCard className="p-5 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="h-10 w-10 rounded-md bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <Badge variant="glass" className="text-[9px]">{LABEL_FOR_CATEGORY[c.category]}</Badge>
                    </div>
                    <div className="space-y-1 flex-1">
                      <h3 className="text-sm font-semibold leading-tight">{c.displayName}</h3>
                      <p className="text-[11px] text-muted-foreground">
                        {direct ? 'Direct connect - give a read-only login and scan now.' : 'Scanned by an Edge pod inside your network.'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between pt-3 mt-3 border-t border-border/30">
                      <div className="flex flex-wrap gap-1">
                        {direct
                          ? <Badge variant="success" className="text-[9px]">Direct scan</Badge>
                          : <Badge variant="glass" className="text-[9px]">Edge pod</Badge>}
                        {c.activityCapability && Object.values(c.activityCapability).some(Boolean) && <Badge variant="glass" className="text-[9px]">Activity</Badge>}
                      </div>
                      {direct ? (
                        <button onClick={() => setConnectTarget(c)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:gap-2 transition-all">
                          Add my {shortName(c.displayName)} <ArrowRight className="h-3 w-3" />
                        </button>
                      ) : (
                        <Link to="/admin/edge-fleet" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                          Edge pod <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-12">No data sources match the selected category.</div>
          )}

          <div className="mt-6 glass-lite rounded-md p-4 text-xs text-muted-foreground flex items-start gap-3">
            <ScanLine className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <span className="font-medium text-foreground/80">How to connect:</span>{' '}
              For <strong className="text-foreground/80">PostgreSQL, SQL Server, or Oracle</strong> reachable from FOCTTA, click
              {' '}<strong className="text-foreground/80">Add my …</strong>, paste a read-only login, and Discovery enumerates every
              schema/table/column and flags PII immediately. Sources inside a private network (or non-relational stores like S3,
              Snowflake, M365) are scanned by an{' '}
              <Link to="/admin/edge-fleet" className="text-primary hover:underline">Edge pod</Link> you provision in your VPC - raw
              values never leave your boundary.
            </div>
          </div>
        </>
      )}

      {connectTarget && (
        <ConnectModal source={connectTarget} onClose={() => setConnectTarget(null)} />
      )}
    </PageContainer>
  );
}

/** "PostgreSQL (incl. RDS, …)" → "PostgreSQL" for the button label. */
function shortName(displayName: string): string {
  return displayName.replace(/\s*\(.*$/, '').trim();
}

/** The real connect flow: catalog-crawl form → POST /v1/discovery/catalog-crawl. */
function ConnectModal({ source, onClose }: { source: DiscoveryConnector; onClose: () => void }) {
  const vendor = CRAWL_VENDOR[source.kind] as CrawlVendor;
  const [form, setForm] = useState({
    host: '', port: DEFAULT_PORT[vendor], database: '', user: '', password: '', includeSchemas: '', sampleValues: false,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CatalogCrawlSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const ready = form.host.trim() && form.database.trim() && form.user.trim() && form.password.length > 0;

  async function onConnect() {
    setBusy(true); setErr(null); setResult(null);
    try {
      const schemas = form.includeSchemas.split(',').map((s) => s.trim()).filter(Boolean);
      const res = await catalogCrawl({
        connection: {
          vendor, host: form.host.trim(), port: Number(form.port), database: form.database.trim(),
          user: form.user.trim(), password: form.password,
          ...(schemas.length ? { includeSchemas: schemas } : {}),
        },
        scanMode: 'full',
        ...(form.sampleValues ? { sampleValues: true } : {}),
      });
      setResult(res.crawl);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Connection failed');
    } finally {
      setBusy(false);
    }
  }

  const input = 'w-full bg-muted/30 border border-border/50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary/60';
  const labelCls = 'text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Connect {shortName(source.displayName)}</DialogTitle>
          <DialogDescription>Read-only credentials. Discovery enumerates the schema and flags PII - raw values are not stored.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-md border border-success/40 bg-success/5 p-4">
              <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">Connected &amp; scanned</div>
                <div className="text-muted-foreground mt-1 leading-relaxed">
                  Discovered <strong className="text-foreground/90">{result.assetsCreated + result.assetsUpdated}</strong> table(s) across{' '}
                  {result.schemas.length} schema(s), registered <strong className="text-foreground/90">{result.columnsRegistered}</strong> columns,
                  and flagged <strong className="text-foreground/90">{result.piiFindings}</strong> PII finding(s).
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" asChild><Link to="/dspm/inventory">View inventory <ArrowRight className="h-3 w-3" /></Link></Button>
              <Button size="sm" variant="glass" asChild><Link to="/dspm/findings">View findings</Link></Button>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className={labelCls}>Host</label>
                <input className={input} placeholder="db.example.internal" value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Port</label>
                <input className={input} type="number" value={form.port} onChange={(e) => setForm((f) => ({ ...f, port: Number(e.target.value) }))} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Database{vendor === 'oracle' ? ' / Service' : ''}</label>
              <input className={input} placeholder={vendor === 'oracle' ? 'ORCLPDB1' : 'app_production'} value={form.database} onChange={(e) => setForm((f) => ({ ...f, database: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>User (read-only)</label>
                <input className={input} autoComplete="off" value={form.user} onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input className={input} type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Schemas (optional, comma-separated)</label>
              <input className={input} placeholder="public, sales" value={form.includeSchemas} onChange={(e) => setForm((f) => ({ ...f, includeSchemas: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={form.sampleValues} onChange={(e) => setForm((f) => ({ ...f, sampleValues: e.target.checked }))} />
              Deep scan - sample values + checksum classifiers (Aadhaar/PAN/card) to catch PII in badly-named columns.
            </label>

            {err && (
              <div className="flex items-start gap-2 rounded-md border border-sev-high/40 bg-sev-high/5 p-3 text-xs">
                <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
                <div><div className="font-medium">Could not connect</div><div className="text-muted-foreground mt-0.5">{err}</div></div>
              </div>
            )}

            <div className="rounded-md bg-muted/20 p-2.5 text-[11px] text-muted-foreground flex items-start gap-2">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-success" />
              The DB host must be reachable from FOCTTA. For a database inside a private network, use an{' '}
              <Link to="/admin/edge-fleet" className="text-primary hover:underline">Edge pod</Link> instead.
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={onConnect} disabled={!ready || busy}>
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Connecting…</> : <><ScanLine className="h-4 w-4" /> Connect &amp; scan</>}
              </Button>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}><X className="h-4 w-4" /> Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
